import {
  AttachInternals,
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
} from '@stencil/core'

import { CssClassMap } from '../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/step?tab=designer).
 *
 * @slot default - Slot para os itens do step (br-step-item), aceitando apenas esse componente.
 *
 * @part container - Parte para o contêiner principal envolvendo todos os step-items, usada para estilizar layout geral via ::part().
 *
 */
@Component({
  tag: 'br-step',
  styleUrl: 'step.scss',
  shadow: true,
  formAssociated: true,
})
export class Step {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrStepElement

  @AttachInternals() elementInternals: ElementInternals

  /**
   * propriedade responsável por definir a orientação do componente Step
   */
  @Prop({ reflect: true }) layout: 'horizontal' | 'vertical' = 'horizontal'
  /**
   *
   * define o passo que começará destacado
   */
  @Prop({ reflect: true }) initialStep: string = '0'
  /**
   *
   * Propriedade que  define a posição onde o texto de destaque,label, ficará localizado no stepItem
   */
  @Prop({ reflect: true }) labelPosition?: 'top' | 'right' | 'bottom' | 'left' = 'bottom'
  /**
   *
   * Propriedade que define o tipo de conteúdo a ser exibido dentro do componente step  e stepItem
   */
  @Prop({ reflect: true }) contentType: 'default' | 'br-icon' | 'no-content' | 'slotted' = 'default'
  /**
   *
   * Propriedade que define o tipo de comportamento que o componente Step deverá seguir
   */
  @Prop({ reflect: true }) mode: 'controller' | 'step' = 'step'

  /**
   *
   * Propriedade que define o tipo de progressão de etapas que o step vai executar
   */
  @Prop({ reflect: true }) progressionType: 'linear' | 'nonlinear' = 'nonlinear'

  @State() activeStep: number = 0
  @State() stepItems: Element[] = []
  /**
   * Emite um evento após o componente ter sido carregado pela primeira vez.
   * Utilize este evento para realizar ações que devem ocorrer depois que o componente foi totalmente carregado.
   * Consulte a documentação do [Stencil](https://stenciljs.com/docs/component-lifecycle#componentdidload) para mais detalhes.
   */
  @Event() brDidLoad: EventEmitter<void>
  componentDidLoad() {
    this.setStepItemsLabelPosition()
    this.setStepItemsContentType()
    this.setStepItemsDefaultContent()
    this.setStepItemsMode(this.mode)
    if (this.mode === 'step') {
      this.configureTimeLine()
    }
    if (this.progressionType === 'linear') {
      this.configureLinearProgression()
    }
    if (this.activeStep <= this.stepItems.length - 1) {
      this.stepItems[this.activeStep].setAttribute('active', 'true')
    } else {
      console.warn('br-step initial-step attribute has value more than number of step-items provided')
    }
    this.brDidLoad.emit()
  }

  /**
   * Emite um evento antes do componente ser renderizado pela primeira vez, permitindo a preparação de dados ou inicializações necessárias.
   * Utilize este evento para realizar ações que devem ocorrer depois que o componente foi totalmente carregado.
   * Consulte a documentação do [Stencil](https://stenciljs.com/docs/component-lifecycle#componentdwillLoad) para mais detalhes.
   */
  @Event() brWillLoad: EventEmitter<void>
  componentWillLoad() {
    this.activeStep = Number(this.initialStep)
    this.getStepItemsElements()
    this.brWillLoad.emit()
  }

  /**
   * Emite um evento para indicar se o componente deve ser atualizado ou não.
   * Este evento é chamado antes de qualquer atualização do componente para decidir se a atualização deve prosseguir.
   * Consulte a documentação do [Stencil](https://stenciljs.com/docs/component-lifecycle#componentshouldupdate) para mais detalhes.
   */
  @Event() brShouldUpdate: EventEmitter<void>

  /**
   * Emite um evento quando o step ativo muda.
   * O evento carrega o índice do novo step ativo (0-based).
   */
  @Event() brStepChange: EventEmitter<{ activeStep: number }>

  /** Controla atualizações pendentes para evitar race conditions */
  private updateScheduled = false

  componentShouldUpdate(newValue: number, oldValue: number, stateName: string) {
    if (stateName === 'activeStep' && oldValue !== newValue) {
      if (!this.updateScheduled) {
        this.updateScheduled = true
        requestAnimationFrame(() => {
          this.updateScheduled = false
          this.updateStepItemsActiveState(oldValue)
        })
      }
      this.brShouldUpdate.emit()
      this.brStepChange.emit({ activeStep: this.activeStep })
    }
  }

  /**
   * Atualiza os atributos 'active' dos step-items de forma segura
   * Chamado via requestAnimationFrame para garantir que o DOM é atualizado após batched state changes
   */
  private updateStepItemsActiveState(previousActiveStep: number) {
    const outOfRange = previousActiveStep > this.stepItems.length - 1

    if (!outOfRange) {
      this.stepItems[previousActiveStep].removeAttribute('active')
    }

    this.stepItems[this.activeStep].setAttribute('active', 'true')
  }

  /**
   * listener para ouvir o evento de click no componente step
   * Através desse listener o componente define qual será o proximo passo do step que terá o estado ativo
   */

  @Listen('click', { capture: true })
  handleClick(event) {
    const targetElement = event.target as Element
    if (targetElement.getAttribute('slot') !== null) {
      const slotItem = this.stepItems.findIndex((element) => element.lastElementChild === targetElement)
      this.activeStep = slotItem
      return
    }
    const item = this.stepItems.findIndex((element) => element === targetElement)
    this.activeStep = item
  }

  private getStepItemsElements() {
    const slotItems = Array.from(this.el.children)
    const firstChildrenContentTypeValue = slotItems[0].getAttribute('content-type')
    const childrenHaveConsistentContentType = slotItems.every(
      (item) => item.getAttribute('content-type') === firstChildrenContentTypeValue
    )
    const childrenHaveConsistentTagName = slotItems.every((item) => item.tagName.toLowerCase() === 'br-step-item')
    if (childrenHaveConsistentContentType && childrenHaveConsistentTagName) {
      this.stepItems = slotItems
      return
    }
    if (!childrenHaveConsistentTagName) {
      throw new Error('br-step component must have as child elements only br-step-items components')
    }
    if (!childrenHaveConsistentContentType) {
      throw new Error('br-step must have all child elements with same content type attribute value')
    }
  }

  private setStepItemsLabelPosition() {
    const slotItems = Array.from(this.el.getElementsByTagName('br-step-item'))
    slotItems.forEach((elements) => {
      if (this.labelPosition !== undefined) {
        elements.setLabelPosition(this.labelPosition)
      }
    })
  }

  private setStepItemsDefaultContent() {
    const slotItems = Array.from(this.el.getElementsByTagName('br-step-item'))
    slotItems.forEach((elements, index) => {
      const indicatorContent = index + 1
      elements.setContent(indicatorContent.toString())
    })
  }

  private async setStepItemsContentType() {
    const slotItems = Array.from(this.el.getElementsByTagName('br-step-item'))
    await Promise.all(slotItems.map((element) => element.setContentType(this.contentType)))
    slotItems.forEach((elements, index) => {
      elements.setAttribute('aria-posinset', `${index + 1}`)
      elements.setAttribute('aria-setsize', `${slotItems.length}`)
    })
  }

  private setStepItemsMode(mode: 'controller' | 'step') {
    const slotItems = Array.from(this.el.getElementsByTagName('br-step-item'))
    slotItems.forEach((elements) => {
      elements.setMode(mode)

      if (mode === 'controller') {
        elements.setContentType('no-content')
        if (this.layout !== 'horizontal') {
          this.layout = 'horizontal'
        }
        elements.removeAttribute('label')
      }
    })
  }

  private getStepItemPosition(stepItemsElements: HTMLBrStepItemElement[], index) {
    if (index === 0) {
      return 'first'
    }
    if (index > 0 && index < stepItemsElements.length - 1) {
      return 'middle'
    }
    return 'last'
  }

  private configureTimeLine() {
    const slotItems = Array.from(this.el.getElementsByTagName('br-step-item'))
    slotItems.forEach((elements, index) => {
      const elementPosition = this.getStepItemPosition(slotItems, index)
      elements.setStepItemPositionStatus(elementPosition)
      elements.handleShowTimeLine(true, this.layout)
    })
  }

  private configureLinearProgression() {
    const slotItems = Array.from(this.el.getElementsByTagName('br-step-item'))
    slotItems.forEach((elements, index) => {
      if (index > 0) {
        elements.setAttribute('disabled', 'true')
      }
    })
  }

  /**
   *
   * Método disponibilizado via API do elemento que controla o componente Step, responsável por avançar para o próximo passo e atualizar o valor do estado activeStep
   */
  @Method()
  async ProceedToNextStep() {
    if (this.activeStep + 1 < this.stepItems.length) {
      this.activeStep = this.activeStep + 1
      if (this.progressionType === 'linear') {
        this.stepItems[this.activeStep].removeAttribute('disabled')
      }
    }
  }

  /**
   *
   * Método disponibilizado via API do elemento que controla o componente Step, responsável por retornar ao passo anterior e atualizar o valor do estado activeStep
   */
  @Method()
  async BackToPreviousStep() {
    if (this.activeStep - 1 >= 0) {
      this.activeStep = this.activeStep - 1
    }
  }

  /**
   *
   * Método disponibilizado via API do elemento que integra um componente externo ao componente Step, permitindo sinalizar validações ou destacar a etapa com estado ativo.
   */
  @Method()
  async StepValidation(validationStatus: 'success' | 'info' | 'danger' | 'warning') {
    this.stepItems[this.activeStep].setAttribute('highlight', validationStatus)
  }

  /**
   *
   * Método disponibilizado via API do elemento que permite a um componente externo obter a etapa com o estado ativo do componente Step.
   */
  @Method()
  async GetActiveStep() {
    return this.activeStep
  }

  private getCssClassMap(): CssClassMap {
    return {
      'br-step': true,
      horizontal: this.layout === 'horizontal',
      vertical: this.layout === 'vertical',
    }
  }

  private AriaObjectContent = {
    step: 'Modo etapas ',
    controller: 'Modo controle ',
    default: 'passos com conteúdo numérico ',
    'br-icon': 'passos com conteúdo que possuem icones ',
    slotted: 'passos com conteúdo customizados ',
    'no-content': 'passos que apresentam conteúdo por meio de tooltip ',
    linear: 'progressão linear ',
    nonlinear: 'progressão não linear ',
    vertical: 'com layout vertical ',
    horizontal: 'com layout horizontal ',
  }

  private getAreaLabel(): string {
    let ariaLabel = ' Lista de Opções '
    if (this.mode !== 'controller') {
      ariaLabel +=
        this.AriaObjectContent[this.mode] +
        this.AriaObjectContent[this.contentType] +
        this.AriaObjectContent[this.progressionType] +
        this.AriaObjectContent[this.layout]
    } else {
      ariaLabel += this.AriaObjectContent[this.mode]
    }
    return ariaLabel
  }

  render() {
    return (
      <Host>
        {this.stepItems.length > 0 && (
          <div
            part="container"
            role="listbox"
            tabIndex={0}
            aria-orientation={this.layout}
            aria-label={this.getAreaLabel()}
            class={this.getCssClassMap()}
            data-label={this.labelPosition}
          >
            <div class="step-progress">
              <slot></slot>
            </div>
          </div>
        )}
      </Host>
    )
  }
}
